/**
 * A WEB APP MANIFEST FOR THE PAGE YOU ARE ON.
 *
 * Two platforms want opposite things from one static file, and this is how
 * both get what they need.
 *
 * iOS takes "Add to Home Screen" from the manifest's `start_url` once a
 * manifest exists — so a fixed `start_url: "/"` means every icon, from every
 * page, opens the Fias root. That is what made it impossible to put Flappyland
 * itself on a home screen (Tom, 2026-09-10: "it removes the Flappyland from
 * the URL").
 *
 * Dropping `start_url` fixed iOS, because the spec then defaults it to the
 * document URL. But Chrome REQUIRES it, and dropping it silently cost Android
 * its install prompt. Measured, not assumed — `Page.getInstallabilityErrors`
 * on deployed staging returned `start-url-not-valid`
 * (`scripts/check-installability.mjs` reproduces it).
 *
 * A manifest whose `start_url` IS the current page satisfies both: Chrome gets
 * a valid `start_url`, and the icon opens the page it was added from either
 * way. On a static site that has to be generated at runtime, and the delivery
 * mechanism is not a free choice — Chrome rejects a `blob:` manifest
 * (`start-url-not-valid`, since relative URLs have no base inside one) and
 * accepts a `data:` manifest. Both were measured with the same probe.
 *
 * Every URL emitted here is therefore ABSOLUTE. Inside a `data:` URL there is
 * no sensible base to resolve a relative path against, and an absolute one
 * removes the question.
 *
 * No `id` field, deliberately: without one a manifest's identity IS its
 * `start_url`, so each page installs as its own app rather than replacing the
 * last. That is the point — Flappyland gets its own icon.
 *
 * An arche page also passes an IDENTITY (its name and its icon), because an
 * icon added from Flappyland should say Flappyland and show Flappyland's art,
 * not be a second copy of Fias. Everything in this module stays a pure
 * function of its arguments; who is on screen is tracked next to the component
 * that writes the DOM, in `components/PageManifestLink.tsx`.
 */

/**
 * Everything about the manifest that does NOT depend on the page.
 *
 * Duplicated from `public/manifest.webmanifest`, which is SAFARI'S copy: it
 * fetches the manifest once during page load and never re-reads, so the static
 * file is the only one it ever sees. That file deliberately omits `start_url`
 * so Safari falls back to the document URL; Chrome, which re-reads the live
 * link, gets its `start_url` from the `data:` URL built below.
 * `page-manifest.test.ts` pins the two together on every other field — the
 * risk of any deliberate duplication is drift.
 */
export const PAGE_MANIFEST_BASE = {
  name: 'Fias',
  short_name: 'Fias',
  description:
    'Fias is a marketplace where AI agents are metered, priced, and paid per invocation.',
  display: 'standalone',
  background_color: '#ffffff',
  theme_color: '#ffffff',
  icons: [
    { src: '/fias-logo-image.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
    { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png', purpose: 'any' },
    { src: '/favicon-32x32.png', sizes: '32x32', type: 'image/png', purpose: 'any' },
  ],
} as const;

/** One icon, shaped as the manifest wants it. */
export interface PageManifestIcon {
  src: string;
  sizes: string;
  type?: string;
}

/**
 * Who the current page IS, when it is something more specific than Fias.
 *
 * `icons` may be empty — an arche with a name but no uploaded icon still gets
 * its name on the home screen, and keeps the Fias artwork.
 */
export interface PageManifestIdentity {
  name: string;
  icons: PageManifestIcon[];
}

/**
 * The manifest for one page, as a plain object.
 *
 * `pathname` only — no query, no hash. A home-screen icon is a durable thing
 * and should not carry a one-off `?tab=` or an auth callback's `?code=`; those
 * would install a URL that means nothing tomorrow, and in the auth case would
 * bake a spent credential into an icon.
 */
export function buildPageManifest(
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): Record<string, unknown> {
  // `new URL` leaves an already-absolute URL alone, so arche icons (which come
  // from the icon CDN, a different origin) pass through untouched.
  const abs = (p: string) => new URL(p, origin).toString();
  const icons = identity?.icons.length ? identity.icons : PAGE_MANIFEST_BASE.icons;
  return {
    ...PAGE_MANIFEST_BASE,
    ...(identity ? { name: identity.name, short_name: identity.name } : {}),
    start_url: abs(pathname || '/'),
    scope: abs('/'),
    icons: icons.map((i) => ({ ...i, src: abs(i.src) })),
  };
}

/** The `data:` URL a `<link rel="manifest">` points at for this page. */
export function pageManifestHref(
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): string {
  const json = JSON.stringify(buildPageManifest(origin, pathname, identity));
  return `data:application/manifest+json,${encodeURIComponent(json)}`;
}

/**
 * Point the document's manifest link at this page. Returns false when there is
 * no link to point (nothing to do, and nothing to throw about).
 *
 * Idempotent: re-running with the same path rewrites the same href.
 */
export function applyPageManifest(
  doc: Document,
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): boolean {
  const link = doc.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) return false;
  link.href = pageManifestHref(origin, pathname, identity);
  return true;
}

/**
 * The size iOS asks for in `<link rel="apple-touch-icon">`.
 *
 * iOS takes the home-screen icon from this tag, NOT from the manifest, so the
 * manifest work above does nothing for it on its own. Arches with a raster
 * icon and every Store listing offer an exact, opaque 180 (`apple-touch-icon`);
 * the rule — the smallest icon at least this big — picks it, and falls back to
 * the next size (iOS scales it down) for an icon without one.
 */
const APPLE_TOUCH_ICON_PX = 180;

/** The first icon at least `APPLE_TOUCH_ICON_PX` square, smallest first. */
export function pickAppleTouchIcon(icons: PageManifestIcon[]): PageManifestIcon | null {
  const sized = icons
    .map((icon) => ({ icon, px: Number.parseInt(icon.sizes.split('x')[0] ?? '', 10) }))
    .filter((c) => Number.isFinite(c.px) && c.px >= APPLE_TOUCH_ICON_PX)
    .sort((a, b) => a.px - b.px);
  return sized[0]?.icon ?? null;
}

/**
 * Each document's own apple-touch-icon, remembered the first time we replace
 * it so leaving an arche restores exactly what `index.html` shipped rather
 * than a path repeated here (which would rot the moment that file changes).
 *
 * Keyed by Document rather than held in one variable: there is only ever one
 * document in the app, but a module-level value would leak between tests and
 * make the first test to run decide what every later one restores to.
 */
const platformAppleTouchIcons = new WeakMap<Document, { href: string; sizes: string }>();

/**
 * Point `<link rel="apple-touch-icon">` at this page's icon, or back at the
 * platform's when `identity` is null. Returns false when there is no such tag.
 */
export function applyAppleTouchIcon(
  doc: Document,
  identity?: PageManifestIdentity | null,
): boolean {
  const link = doc.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (!link) return false;
  let platform = platformAppleTouchIcons.get(doc);
  if (!platform) {
    platform = {
      href: link.getAttribute('href') ?? '',
      sizes: link.getAttribute('sizes') ?? '',
    };
    platformAppleTouchIcons.set(doc, platform);
  }
  const chosen = identity ? pickAppleTouchIcon(identity.icons) : null;
  link.setAttribute('href', chosen ? chosen.src : platform.href);
  // The `sizes` attribute must keep describing the file it points at.
  if (chosen) {
    link.setAttribute('sizes', chosen.sizes);
  } else if (platform.sizes) {
    link.setAttribute('sizes', platform.sizes);
  } else {
    link.removeAttribute('sizes');
  }
  return true;
}

/**
 * Each document's own `apple-mobile-web-app-title`, remembered the first time
 * it is replaced — the same restore-what-shipped rule as the touch icon above.
 */
const platformAppleWebAppTitles = new WeakMap<Document, string>();

/**
 * Point `<meta name="apple-mobile-web-app-title">` — the name iOS suggests in
 * "Add to Home Screen" — at this page's name, or back at the platform's when
 * `identity` is null. Without it every arche and game suggested "Fias".
 * Returns false when there is no such tag.
 */
export function applyAppleWebAppTitle(
  doc: Document,
  identity?: PageManifestIdentity | null,
): boolean {
  const meta = doc.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (!meta) return false;
  if (!platformAppleWebAppTitles.has(doc)) {
    platformAppleWebAppTitles.set(doc, meta.getAttribute('content') ?? '');
  }
  meta.setAttribute('content', identity?.name || platformAppleWebAppTitles.get(doc) || '');
  return true;
}

/**
 * What `pinsOnlyAtPageLoad` reads off the browser — `navigator`'s fields, plus
 * whether the page is the top-level document. A parameter rather than globals
 * so the rule is a pure function the tests can drive.
 */
export interface PinEnvironment {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  /** iOS's own flag: true when the page was launched from the home screen. */
  standalone?: boolean;
  topLevel: boolean;
}

/**
 * True when "Add to Home Screen" can only pin the address the page was LOADED
 * at — every browser on iPhone and iPad, outside an installed app.
 *
 * Safari fetches the manifest once during page load and never re-reads it (see
 * this module's header and `tests/architecture/installable-pwa.test.ts`): with
 * no `start_url` in the static file it pins the document URL as it was when
 * the page LOADED. An arche reached by in-app navigation (react-router's
 * `navigate` — a link on the Fias home, the store, one arche opening another)
 * never loads its own address, so its icon opens wherever the tab first
 * loaded (Tom, 2026-10-16: "It doesn't link to the correct URL"; 2026-10-17,
 * LitCraft itself: "it still doesn't give the correct URL"). Flappyland only
 * ever worked because players open it by a direct load of its address. Where
 * this is true, an arche's page that was not the page loaded loads itself
 * (`archeNeedsPageLoad` below, asked by the arche route guard).
 *
 * Every browser on iOS is WebKit, so Chrome or Firefox there is the same case,
 * and an iPad in desktop mode reports itself as a Mac (`MacIntel`) — the touch
 * points tell it apart. Not inside an installed app (`standalone`): the icon
 * already exists, and a reload there would only cost time. Not in a frame
 * (an embed): there is no home screen to add a frame to.
 */
export function pinsOnlyAtPageLoad(env: PinEnvironment): boolean {
  if (!env.topLevel || env.standalone === true) return false;
  if (/\b(iPhone|iPad|iPod)\b/.test(env.userAgent)) return true;
  return env.platform === 'MacIntel' && env.maxTouchPoints > 1;
}

/** `pinsOnlyAtPageLoad` for this browser, now. False wherever it cannot tell. */
export function browserPinsOnlyAtPageLoad(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  let topLevel = false;
  try {
    topLevel = window.top === window.self;
  } catch {
    // a cross-origin parent throws on `top` access in some browsers: a frame
  }
  return pinsOnlyAtPageLoad({
    userAgent: navigator.userAgent ?? '',
    platform: navigator.platform ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    standalone: (navigator as Navigator & { standalone?: boolean }).standalone,
    topLevel,
  });
}

// ---------------------------------------------------------------------------
// The page this document was LOADED as
// ---------------------------------------------------------------------------

/**
 * `location.pathname` as it was when this module first ran — before the
 * router, before any in-app navigation: the address Safari pinned the page's
 * manifest to. This module is imported by `App.tsx` (through
 * `PageManifestLink`), so it runs while the entry bundle evaluates; it must
 * stay out of any lazily loaded chunk, or the value would be read after a
 * navigation and name the wrong page.
 */
const PAGE_LOAD_PATHNAME: string | null =
  typeof window === 'undefined' ? null : window.location.pathname;

/**
 * The router's position of a history entry: react-router keeps it in
 * `history.state.idx`, which a replace keeps and a push advances. Null where
 * the state carries none.
 */
export function historyIndexOf(state: unknown): number | null {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}

/**
 * The position of the entry this document was loaded at. A fresh load has no
 * state yet, and the router gives that entry index 0 as it starts; a reload,
 * or a load from Back, finds the entry's own index still in its state.
 */
const PAGE_LOAD_HISTORY_INDEX: number | null =
  typeof window === 'undefined' ? null : (historyIndexOf(window.history.state) ?? 0);

/**
 * True when the page on screen is what the front door showed: the document
 * was loaded at `/` and has only been REPLACED since (the same history entry),
 * as a signed-in visitor's `/` is sent on to Make a Re-Quest. Safari's icon
 * for it opens `/`, which leads back to the same page, so it needs no load of
 * its own — and it is Fias, not the arche it lands on.
 */
export function frontDoorArrival(
  loadedPathname: string | null,
  loadIndex: number | null,
  currentIndex: number | null,
): boolean {
  return loadedPathname === '/' && loadIndex !== null && currentIndex === loadIndex;
}

/** `frontDoorArrival` for this document, now. */
export function shownByFrontDoor(): boolean {
  if (typeof window === 'undefined') return false;
  return frontDoorArrival(
    PAGE_LOAD_PATHNAME,
    PAGE_LOAD_HISTORY_INDEX,
    historyIndexOf(window.history.state),
  );
}

/** The `<segment>` of an `/a/<segment>[/…]` path, decoded; null for any other path. */
export function archeSegmentOf(pathname: string | null): string | null {
  const match = pathname?.match(/^\/a\/([^/]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * The part of an `/a/<segment>[/…]` path after the arche, with its leading
 * slash and without a trailing one: `/games/wordtrace`, or `''` for the arche
 * itself. Null for any other path.
 */
export function archeSubPathOf(pathname: string | null): string | null {
  const match = pathname?.match(/^\/a\/[^/]+(\/.*)?$/);
  if (!match) return null;
  return (match[1] ?? '').replace(/\/+$/, '');
}

/**
 * Which of an arche's extra Store listings the page IS for "Add to Home
 * Screen" — Wordtrace on `/a/<flappyland>/games/wordtrace` — or null for the
 * arche itself.
 *
 * Decided by the address the icon will OPEN. Where "Add to Home Screen" pins
 * only the page load (`browserPinsOnlyAtPageLoad`) that is the address the
 * document was loaded at, so a game reached from inside the arche (no load of
 * its own) pins as the arche — naming it after the game would put Wordtrace's
 * name and art on an icon that opens Flappyland. Elsewhere the manifest
 * follows the page, and so does this.
 */
export function pinnedListing<L extends { path: string }>(
  listings: readonly L[],
  segment: string,
  arche: { archId: string; slug?: string | null },
  currentPathname: string,
  pinsOnlyAtLoad: boolean = browserPinsOnlyAtPageLoad(),
  loadedPathname: string | null = PAGE_LOAD_PATHNAME,
): L | null {
  if (listings.length === 0) return null;
  const pinned = pinsOnlyAtLoad
    ? loadedAtArche(loadedPathname, segment, arche)
      ? loadedPathname
      : null
    : currentPathname;
  const subPath = archeSubPathOf(pinned);
  if (!subPath) return null;
  return listings.find((l) => l.path === subPath) ?? null;
}

/**
 * True when a page loaded at `loadedPathname` was a page of this arche: its
 * `/a/<segment>` is the segment now on screen, or the arche's id, or its slug.
 * The id and the slug both count because the route guard rewrites one onto the
 * other in place (`canonicalArcheSegment`) — a page loaded at `/a/arc_<id>`
 * and shown at `/a/<slug>` is the same arche, and Safari's icon for it opens
 * the arche either way.
 */
export function loadedAtArche(
  loadedPathname: string | null,
  segment: string,
  arche: { archId: string; slug?: string | null },
): boolean {
  const loaded = archeSegmentOf(loadedPathname);
  if (loaded === null) return false;
  return loaded === segment || loaded === arche.archId || (!!arche.slug && loaded === arche.slug);
}

/**
 * Where the last `loadArchePage` sent this tab, read once as this document
 * loads (and cleared): the guard against reloading the same address twice.
 */
const RELOAD_MARK = 'fias:home-screen-load';
const RELOADED_AT: string | null = (() => {
  try {
    const at = window.sessionStorage.getItem(RELOAD_MARK);
    window.sessionStorage.removeItem(RELOAD_MARK);
    return at;
  } catch {
    return null; // no window (server render) or no storage
  }
})();

/**
 * True when the arche page now on screen must load itself so "Add to Home
 * Screen" pins IT: a browser that pins only the page-load address
 * (`browserPinsOnlyAtPageLoad`), on a document that was loaded as some other
 * page. `segment` is the `/a/<segment>` on screen; `arche` what it resolved to.
 *
 * An arche opening another arche makes the load itself, before it navigates
 * (`leaveForArche` below), so the page it leaves is not emptied first; this is
 * for every other way into an arche.
 *
 * Not for what the front door showed (`shownByFrontDoor`): its icon opens `/`,
 * which comes back here — and a reload there would cost every visit from `/`
 * a second start. Never for the address this document was itself reloaded at:
 * one load per arrival, whatever the comparisons conclude, so a page can never
 * reload itself in a loop.
 */
export function archeNeedsPageLoad(
  segment: string,
  arche: { archId: string; slug?: string | null },
): boolean {
  if (!browserPinsOnlyAtPageLoad()) return false;
  if (RELOADED_AT !== null && RELOADED_AT === window.location.pathname) return false;
  if (shownByFrontDoor()) return false;
  return !loadedAtArche(PAGE_LOAD_PATHNAME, segment, arche);
}

/**
 * Load the page on screen for real — a reload of the current history entry,
 * which keeps its navigation state (a handoff riding in it arrives after the
 * load, as it would have before). Does nothing where the reload could not be
 * marked (no storage): an icon on the wrong address is better than a page that
 * might reload forever.
 */
export function loadArchePage(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARK, window.location.pathname);
  } catch {
    return;
  }
  window.location.reload();
}

// ---------------------------------------------------------------------------
// Keeping the page an arche leaves (iPhone and iPad)
// ---------------------------------------------------------------------------

/*
 * Where "Add to Home Screen" pins only the page load (`pinsOnlyAtPageLoad`),
 * every arche on screen must have been loaded as itself (`archeNeedsPageLoad`
 * above). For an arche that opens other arches — the LitCraft hub opening its
 * games — that made every trip a page load BOTH ways, and the way back started
 * the hub from nothing each time (Tom, 2026-10-18: "When I leave and go back to
 * the LitCraft game on my phone it reloads every time. Flappyland doesn't do
 * that" — Flappyland's games are pages of Flappyland, so it never leaves
 * itself). The open went in place first, which emptied the arche's page, and
 * the route guard then reloaded the tab at the new address.
 *
 * So, on those browsers, for an arche that opens another (`open_arche`,
 * `openArcheFromHost`):
 *
 *  - LEAVING is a real page load made from the arche's own page
 *    (`leaveForArche`) — the load the guard would have made anyway, one step
 *    sooner, and without first emptying the page being left. That page is left
 *    whole, which is what lets Safari keep it in its back-forward cache.
 *  - COMING BACK to an arche this tab left that way goes back through the tab's
 *    history to the very entry it was left from (`returnToArche`). Where Safari
 *    still holds that page it comes back as it was left: no load, the game or
 *    menu the player had open still open. Where it does not, that entry loads —
 *    what the way back cost before.
 *
 * Either way the page on screen was LOADED as the arche on screen, so the icon
 * rule holds and the guard has nothing to reload. Every other way into an arche
 * (a link, the store, the assistant) is unchanged and still goes through the
 * guard. Not in an installed app, where nothing reloads to begin with.
 *
 * The way back goes BY ENTRY, through the Navigation API (`navigation.entries`,
 * `navigation.traverseTo`), never by counting steps for `history.go`: a plugin
 * frame's own loads add entries to the tab's history that the router never
 * sees, so a count lands inside the page instead of behind it (measured in
 * WebKit: one step back from a game reopened the game). An arche's page notes
 * its entry's key as it leaves; the next page reads that once, as it loads,
 * with the entries that page had itself arrived through. A browser without the
 * Navigation API, or an entry it no longer holds, opens the arche by a load.
 */

/** One arche page this tab left, by `leaveForArche`, on the way to this one. */
export interface ArcheTrailStep {
  /** The arche's id, as the bridge stamps it on `open_arche` (`originArcheId`). */
  archeId: string;
  /** The Navigation API key of the history entry it was left from. */
  key: string;
}

/** The part of the Navigation API used here (not in this TypeScript's DOM types yet). */
interface TrailNavigation {
  currentEntry: { key: string } | null;
  entries(): { key: string }[];
  traverseTo(key: string): { committed: Promise<unknown>; finished: Promise<unknown> };
}

/** The Navigation API, where the browser has it. */
function tabNavigation(): TrailNavigation | null {
  const nav = (window as unknown as { navigation?: Partial<TrailNavigation> }).navigation;
  return nav &&
    typeof nav.entries === 'function' &&
    typeof nav.traverseTo === 'function' &&
    'currentEntry' in nav
    ? (nav as TrailNavigation)
    : null;
}

const TRAIL_MARK = 'fias:arche-trail';
/** A mark older than this is from some earlier page, not the load that just happened. */
const MARK_MAX_AGE_MS = 60_000;
/** Steps kept. A way back further than this is a fresh open. */
const TRAIL_MAX_STEPS = 8;
const ARCHE_ID = /^[A-Za-z0-9_-]+$/;

function isTrailStep(v: unknown): v is ArcheTrailStep {
  const s = v as Partial<ArcheTrailStep> | null;
  return (
    !!s &&
    typeof s.archeId === 'string' &&
    ARCHE_ID.test(s.archeId) &&
    typeof s.key === 'string' &&
    s.key.length > 0 &&
    s.key.length <= 200
  );
}

/**
 * The trail a page arrived through: the steps in a `leaveForArche` mark that
 * was written for THIS load (its address is the one the page loaded at, and it
 * is fresh) — else none. Pure, for the tests; read once below as the page loads.
 */
export function trailFromMark(
  mark: Record<string, unknown> | null,
  loadedPathname: string | null,
  now: number,
): ArcheTrailStep[] {
  if (!mark || loadedPathname === null || mark.to !== loadedPathname) return [];
  if (typeof mark.at !== 'number' || now - mark.at < 0 || now - mark.at > MARK_MAX_AGE_MS) {
    return [];
  }
  if (!Array.isArray(mark.trail) || !mark.trail.every(isTrailStep)) return [];
  return (mark.trail as ArcheTrailStep[]).slice(-TRAIL_MAX_STEPS);
}

/** The arche pages this tab left to reach this one, oldest first. Read once: the mark is for one page. */
const ARRIVED_THROUGH: readonly ArcheTrailStep[] = (() => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(TRAIL_MARK);
    window.sessionStorage.removeItem(TRAIL_MARK);
    const mark: unknown = raw ? JSON.parse(raw) : null;
    return trailFromMark(
      mark && typeof mark === 'object' ? (mark as Record<string, unknown>) : null,
      PAGE_LOAD_PATHNAME,
      Date.now(),
    );
  } catch {
    return []; // no storage, or not JSON
  }
})();

/**
 * The history entry to go back to for `archeId`: the newest step of the trail
 * for that arche — the page the player last saw it on — while the tab still
 * holds that entry and it is not the one on screen. Null otherwise. Pure.
 */
export function entryToReturnTo(
  trail: readonly ArcheTrailStep[],
  archeId: string,
  heldKeys: readonly string[],
  currentKey: string | null,
): string | null {
  for (let i = trail.length - 1; i >= 0; i--) {
    if (trail[i].archeId !== archeId) continue;
    const { key } = trail[i];
    return key !== currentKey && heldKeys.includes(key) ? key : null;
  }
  return null;
}

/**
 * Leave the page of `leavingArcheId` for `url` (an `/a/…` path) by a real page
 * load, noting the entry it leaves so the arche can be come back to
 * (`returnToArche`). The load happens even where nothing can be noted — it is
 * the load the route guard would otherwise make; only the way back is lost.
 */
export function leaveForArche(url: string, leavingArcheId: string): void {
  const key = tabNavigation()?.currentEntry?.key;
  if (key && ARCHE_ID.test(leavingArcheId)) {
    try {
      window.sessionStorage.setItem(
        TRAIL_MARK,
        JSON.stringify({
          to: url.split(/[?#]/)[0],
          at: Date.now(),
          trail: [...ARRIVED_THROUGH, { archeId: leavingArcheId, key }].slice(-TRAIL_MAX_STEPS),
        }),
      );
    } catch {
      // no storage: the page still loads; coming back will load it too
    }
  }
  window.location.assign(url);
}

/**
 * Go back to the page of `archeId` if this tab left it on the way here, and
 * answer true; false (nothing done) when it did not, so the caller opens it
 * instead. A way back the browser refuses opens `fallbackUrl` by a load.
 */
export function returnToArche(archeId: string, fallbackUrl: string): boolean {
  const nav = tabNavigation();
  if (!nav) return false;
  let key: string | null;
  try {
    key = entryToReturnTo(
      ARRIVED_THROUGH,
      archeId,
      nav.entries().map((e) => e.key),
      nav.currentEntry?.key ?? null,
    );
  } catch {
    return false;
  }
  if (key === null) return false;
  // Only while this page is still the one on screen: once it has gone — into
  // Safari's back-forward cache, say — a promise settling later must never
  // send the tab anywhere.
  let left = false;
  window.addEventListener(
    'pagehide',
    () => {
      left = true;
    },
    { once: true },
  );
  const fallBack = () => {
    if (!left) window.location.assign(fallbackUrl);
  };
  try {
    const { committed, finished } = nav.traverseTo(key);
    committed.catch(fallBack);
    finished.catch(() => {});
  } catch {
    fallBack();
  }
  return true;
}
